/**
 * /jobs — full-history view of background AI jobs.
 *
 * Defaults to the caller's jobs from the last 24h. Admin / super_admin /
 * auditor can flip to all-users scope via the backend ?scope=all flag
 * (route-level RBAC is enforced server-side).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity,
  ChevronsLeft,
  RefreshCw,
  Filter as FilterIcon,
  CalendarClock,
} from 'lucide-react';
import { api } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { cn } from '@/shared/cn';
import { fmtDuration, jobElapsedMs, KIND_LABEL, type Job, type JobKind, type JobStatus } from '@/shared/jobs';
import JobItem from '@/components/JobItem';

const ALL_STATUSES: JobStatus[] = ['PENDING', 'RUNNING', 'DONE', 'FAILED', 'CANCELLED'];
const ALL_KINDS: JobKind[] = [
  'summary',
  'findings',
  'violation',
  'timeline',
  'entity',
  'inconsistency',
  'pii_llm',
  'reindex',
];

const STATUS_COLOR: Record<JobStatus, string> = {
  PENDING: 'text-ink-3',
  RUNNING: 'text-brass',
  DONE: 'text-olive',
  FAILED: 'text-crimson',
  CANCELLED: 'text-ink-3',
};

export default function JobsPage() {
  const { user } = useAuth();
  const canSeeAll = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'auditor';

  const [scope, setScope] = useState<'me' | 'all'>('me');
  const [statusSel, setStatusSel] = useState<Set<JobStatus>>(new Set());
  const [kindSel, setKindSel] = useState<Set<JobKind>>(new Set());
  const [sinceHours, setSinceHours] = useState<number>(24);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const queryKey = ['jobs-page', scope, [...statusSel].sort().join(','), [...kindSel].sort().join(','), sinceHours];
  const { data: jobs, isFetching, refetch } = useQuery<Job[]>({
    queryKey,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('scope', scope);
      qs.set('since_hours', String(sinceHours));
      qs.set('limit', '200');
      for (const s of statusSel) qs.append('status', s);
      if (kindSel.size === 1) {
        // Backend takes a single `kind`; multi-select is filtered client-side
        const only = [...kindSel][0];
        if (only) qs.set('kind', only);
      }
      return api(`/api/v1/jobs?${qs.toString()}`);
    },
    refetchInterval: 5000,
  });

  const filtered = useMemo(() => {
    if (!jobs) return [];
    if (kindSel.size <= 1) return jobs;
    return jobs.filter((j) => kindSel.has(j.kind as JobKind));
  }, [jobs, kindSel]);

  const selected = selectedId ? filtered.find((j) => j.id === selectedId) ?? null : null;

  const stats = useMemo(() => {
    const counts: Record<JobStatus, number> = { PENDING: 0, RUNNING: 0, DONE: 0, FAILED: 0, CANCELLED: 0 };
    for (const j of filtered) counts[j.status as JobStatus] = (counts[j.status as JobStatus] || 0) + 1;
    return counts;
  }, [filtered]);

  const toggleStatus = (s: JobStatus) => {
    setStatusSel((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });
  };
  const toggleKind = (k: JobKind) => {
    setKindSel((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  return (
    <div>
      {/* ─── HEADER ─── */}
      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <Activity className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">Background Jobs</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— OPERA</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">
          {filtered.length} job{filtered.length === 1 ? '' : 's'} · {sinceHours}h
        </span>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider border border-ink-line text-ink-2 hover:border-brass hover:text-brass transition flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={cn('w-3 h-3', isFetching && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {/* ─── STAT STRIP ─── */}
      <div className="grid grid-cols-5 gap-2 mb-4">
        {ALL_STATUSES.map((s) => (
          <div key={s} className="bg-paper-2/40 border border-ink-line p-3">
            <div className={cn('font-display font-bold text-[22px] leading-none tabular-nums', STATUS_COLOR[s])}>
              {stats[s] || 0}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mt-1">{s}</div>
          </div>
        ))}
      </div>

      {/* ─── FILTER BAR ─── */}
      <div className="mb-4 p-3 border border-ink-line bg-paper-2/30 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <FilterIcon className="w-3 h-3 text-ink-3" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Status:</span>
          {ALL_STATUSES.map((s) => {
            const on = statusSel.has(s);
            return (
              <button
                key={s}
                onClick={() => toggleStatus(s)}
                className={cn(
                  'px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border transition',
                  on
                    ? 'bg-ink text-paper border-ink'
                    : 'border-ink-line text-ink-2 hover:border-ink hover:text-ink',
                )}
              >
                {s}
              </button>
            );
          })}
          {statusSel.size > 0 && (
            <button
              onClick={() => setStatusSel(new Set())}
              className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-3 hover:text-crimson"
            >
              clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Kind:</span>
          {ALL_KINDS.map((k) => {
            const on = kindSel.has(k);
            return (
              <button
                key={k}
                onClick={() => toggleKind(k)}
                className={cn(
                  'px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border transition',
                  on
                    ? 'bg-ink text-paper border-ink'
                    : 'border-ink-line text-ink-2 hover:border-ink hover:text-ink',
                )}
              >
                {KIND_LABEL[k]}
              </button>
            );
          })}
          {kindSel.size > 0 && (
            <button
              onClick={() => setKindSel(new Set())}
              className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-ink-3 hover:text-crimson"
            >
              clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarClock className="w-3 h-3 text-ink-3" />
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Window:</span>
          {[1, 6, 24, 72, 168].map((h) => (
            <button
              key={h}
              onClick={() => setSinceHours(h)}
              className={cn(
                'px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border transition',
                sinceHours === h
                  ? 'bg-ink text-paper border-ink'
                  : 'border-ink-line text-ink-2 hover:border-ink hover:text-ink',
              )}
            >
              {h < 24 ? `${h}h` : `${h / 24}d`}
            </button>
          ))}
          {canSeeAll && (
            <>
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 ml-3">Scope:</span>
              {(['me', 'all'] as const).map((sc) => (
                <button
                  key={sc}
                  onClick={() => setScope(sc)}
                  className={cn(
                    'px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider border transition',
                    scope === sc
                      ? 'bg-ink text-paper border-ink'
                      : 'border-ink-line text-ink-2 hover:border-ink hover:text-ink',
                  )}
                >
                  {sc}
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ─── BODY ─── */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        {/* LIST */}
        <div className="border border-ink-line bg-paper">
          {!isFetching && filtered.length === 0 && (
            <div className="text-center py-12 font-mono text-xs text-ink-3">
              <Activity className="w-10 h-10 mx-auto mb-2 opacity-40" />
              Tidak ada job pada filter ini.
              <div className="mt-1 text-[10px]">
                Coba longgarkan window atau filter.
              </div>
            </div>
          )}
          {filtered.map((j) => (
            <button
              key={j.id}
              onClick={() => setSelectedId(j.id)}
              className={cn(
                'block w-full text-left',
                selectedId === j.id && 'bg-paper-2/60',
              )}
            >
              <JobItem job={j} compact />
            </button>
          ))}
        </div>

        {/* DETAIL PANEL */}
        <aside className="border border-ink-line bg-paper-2/30 p-4 self-start sticky top-4 max-h-[80vh] overflow-y-auto">
          {!selected && (
            <div className="font-mono text-[11px] text-ink-3">
              Klik salah satu job untuk melihat detailnya.
            </div>
          )}
          {selected && (
            <div className="space-y-3 font-mono text-[11px]">
              <div>
                <div className="font-display font-bold text-[14px] tracking-tight text-ink">
                  {KIND_LABEL[selected.kind as JobKind]}
                </div>
                <div className="text-ink-3 text-[10px] uppercase tracking-wider mt-0.5">
                  {selected.id}
                </div>
              </div>
              <KV k="Status" v={<span className={STATUS_COLOR[selected.status as JobStatus]}>{selected.status}</span>} />
              <KV k="Stage" v={selected.stage || '—'} />
              <KV
                k="Progress"
                v={selected.progress != null ? `${selected.progress}%` : '—'}
              />
              <KV k="User" v={selected.user_email} />
              <KV k="Doc" v={selected.doc_id ? selected.doc_id : '—'} />
              <KV k="Created" v={new Date(selected.created_at).toLocaleString('id-ID')} />
              {selected.started_at && (
                <KV k="Started" v={new Date(selected.started_at).toLocaleString('id-ID')} />
              )}
              {selected.completed_at && (
                <KV k="Completed" v={new Date(selected.completed_at).toLocaleString('id-ID')} />
              )}
              <KV k="Duration" v={fmtDuration(jobElapsedMs(selected))} />
              {selected.eta_ms != null && (
                <KV k="ETA (median)" v={fmtDuration(selected.eta_ms)} />
              )}
              {selected.celery_task_id && (
                <KV k="Celery" v={selected.celery_task_id} />
              )}
              {selected.params && Object.keys(selected.params).length > 0 && (
                <div>
                  <div className="text-ink-3 uppercase tracking-widest text-[10px] mb-1">Params</div>
                  <pre className="p-2 bg-paper border border-ink-line text-[10px] overflow-x-auto leading-relaxed">
                    {JSON.stringify(selected.params, null, 2)}
                  </pre>
                </div>
              )}
              {selected.result_ref && (
                <div>
                  <div className="text-ink-3 uppercase tracking-widest text-[10px] mb-1">Result ref</div>
                  <pre className="p-2 bg-paper border border-ink-line text-[10px] overflow-x-auto leading-relaxed">
                    {JSON.stringify(selected.result_ref, null, 2)}
                  </pre>
                </div>
              )}
              {selected.error && (
                <div>
                  <div className="text-crimson uppercase tracking-widest text-[10px] mb-1">Error</div>
                  <pre className="p-2 bg-crimson/5 border-2 border-crimson/40 text-[10px] text-crimson overflow-x-auto leading-relaxed whitespace-pre-wrap">
                    {selected.error}
                  </pre>
                </div>
              )}
              {selected.doc_id && (
                <Link
                  to={`/berkas/${selected.doc_id}?view=${selected.kind}&job=${selected.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider border border-brass text-brass hover:bg-brass hover:text-paper transition"
                >
                  <ChevronsLeft className="w-3 h-3 rotate-180" /> Buka BAP
                </Link>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[100px_1fr] gap-2 items-baseline">
      <div className="text-ink-3 uppercase tracking-widest text-[10px]">{k}</div>
      <div className="text-ink break-all">{v}</div>
    </div>
  );
}
